import React from 'react';
import { useTranslation } from '@/hooks/useTranslation';
import { useGame } from '@/app/providers/GameContextProvider';
import { useGlobalPopup } from '@/app/providers/GlobalPopupProvider';
import { useGameModeSettingsContext } from '@/app/providers/GameModeSettingsProvider';
import { FightModeSettings, CustomModeSettings, DamageScaling } from '@/lib/types/common/GameModeAdvancedSettings';
import TooltipWithIconFFXIV from '@/app/components/TooltipWithIconFFXIV';
import ToggleFFXIV from '@/app/components/ToggleFFXIV';

const DamageMultiplierSelector: React.FC = () => {
    const { t } = useTranslation();
    const { room, gameState } = useGame();
    const { showPopup } = useGlobalPopup();
    const { settings, updateSettings, isGameMaster } = useGameModeSettingsContext();

    if (!room || !gameState) return null;

    const value = (settings as FightModeSettings | CustomModeSettings).damageMultiplierScaling ?? 'normal';

    const handleDamageScalingChange = (newScaling: DamageScaling) => {
        if (isGameMaster) {
            updateSettings({ damageMultiplierScaling: newScaling } as Partial<FightModeSettings>);
        } else {
            showPopup({
                type: 'error',
                message: t('lobby.onlyGameMasterCan'),
            });
        }
    };

    return (
        <div className="flex gap-7 items-center">
            <div className="text-[#EEE1C5] text-shadow-border shadow-black text-lg">
                {t('game.config.advancedSettings.damageScalingDifficulty')}
            </div>
            <div className="flex gap-7">
                <ToggleFFXIV
                    value={value === "normal"}
                    onChange={() => handleDamageScalingChange('normal')}
                    shape='circle'
                    text={t('game.config.advancedSettings.damageScalingNormal')}
                />
                <ToggleFFXIV
                    value={value === "hard"}
                    onChange={() => handleDamageScalingChange('hard')}
                    shape='circle'
                    text={t('game.config.advancedSettings.damageScalingHard')}
                />
                <ToggleFFXIV
                    value={value === "extreme"}
                    onChange={() => handleDamageScalingChange('extreme')}
                    shape='circle'
                    text={t('game.config.advancedSettings.damageScalingExtreme')}
                />
            </div>

            <TooltipWithIconFFXIV text={t('game.config.advancedSettings.damageScalingTooltip')} />
        </div>
    );
};

export default DamageMultiplierSelector;
